/**
 * Montants : affichage et arithmétique.
 *
 * **Tous les montants du contrat sont des chaînes décimales**, pas des nombres.
 * Un `Number("9007199254740993")` rend 9007199254740992 — un token évaporé, en
 * silence, sans la moindre exception. Ce fichier est la seule porte par laquelle
 * un montant se calcule : `BigInt` à l'intérieur, chaîne à l'entrée comme à la
 * sortie.
 *
 * Un écran qui écrit `Number(balance.available)` annule l'effort de tout le
 * back. La seule exception admise est `ratio()`, qui rend un nombre parce qu'un
 * pourcentage de jauge n'est pas de la monnaie.
 *
 * **L'unité des tokens est le centième** : le contrat transporte `"350"` pour
 * 3,5 tokens, exactement comme il transporte des centimes pour les euros. Les
 * deux affichages suivent la même règle — deux décimales, toujours — et la
 * division par 100 se fait en `BigInt`, jamais en flottant.
 */
import type { Int64 } from "../api/types";

const FORMAT_ENTIER = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 });

/** Centièmes dans un token : le facteur du contrat, écrit une seule fois. */
export const CENTIEMES_PAR_TOKEN = 100n;

/**
 * Espace fine insécable (U+202F), exigée avant le symbole monétaire en
 * typographie française.
 *
 * Construite par son point de code et jamais écrite en clair : un caractère
 * invisible posé au milieu d'une chaîne est indébogable, se perd au premier
 * copier-coller, et se confond à l'oeil avec une espace ordinaire.
 */
const ESPACE_FINE = String.fromCharCode(0x202f);

/** Espace insécable ordinaire (U+00A0), que certains claviers produisent. */
const ESPACE_INSECABLE = String.fromCharCode(0x00a0);

/**
 * Convertit une chaîne décimale du contrat en `BigInt`.
 *
 * Lève sur une entrée qui n'est pas un entier décimal : mieux vaut un écran qui
 * casse en développement qu'un solde faux affiché en démonstration.
 */
export function toBigInt(value: Int64): bigint {
  return BigInt(value);
}

/** Convertit un `BigInt` en chaîne décimale, forme attendue par l'API. */
export function fromBigInt(value: bigint): Int64 {
  return value.toString(10);
}

/**
 * Écrit une valeur absolue de centièmes en « entier,dd » : `10050n` devient
 * `100,50`, séparateur de milliers français compris.
 *
 * Toujours deux décimales, même sur un montant rond : `57,00` et `57,50` dans
 * la même colonne doivent s'aligner, et un lecteur ne doit jamais se demander
 * si `57` veut dire cinquante-sept tokens ou cinquante-sept centièmes.
 */
function formatCentiemes(absolu: bigint): string {
  const entiers = absolu / CENTIEMES_PAR_TOKEN;
  const centiemes = absolu % CENTIEMES_PAR_TOKEN;
  return `${FORMAT_ENTIER.format(entiers)},${centiemes.toString().padStart(2, "0")}`;
}

/**
 * Formate un montant de tokens reçu en centièmes : `"1000000"` devient
 * `10 000,00`.
 *
 * Un montant négatif garde son signe (le vrai, U+2212), pour les rares
 * différences calculées côté écran.
 */
export function formatTokens(value: Int64): string {
  const n = toBigInt(value);
  return n < 0n ? `−${formatCentiemes(-n)}` : formatCentiemes(n);
}

/**
 * Comme `formatTokens`, mais avec un signe explicite en tête.
 *
 * Utile dans l'historique, où la nature de l'écriture dit déjà le sens du
 * mouvement mais où l'oeil cherche un signe. Le signe négatif employé est le
 * vrai (U+2212), pas un trait d'union.
 *
 * @param sens sens du mouvement quand la valeur elle-même est positive.
 */
export function formatTokensSigned(value: Int64, sens: "+" | "-" = "+"): string {
  const n = toBigInt(value);
  const absolu = formatCentiemes(n < 0n ? -n : n);
  const negatif = sens === "-" ? n >= 0n : n < 0n;
  return `${negatif ? "−" : "+"}${absolu}`;
}

/**
 * Formate des centimes en euros : `"1050000"` devient `10 500,00 EUR`, avec le
 * symbole.
 *
 * La division par 100 se fait en `BigInt`, pas en flottant : rien ne garantit
 * qu'un prix futur reste du bon côté de la frontière des 2^53, et un arrondi
 * sur un prix affiché en démonstration est exactement ce que le projet
 * s'interdit partout ailleurs.
 */
export function formatEuros(cents: Int64): string {
  const n = toBigInt(cents);
  const negatif = n < 0n;
  const absolu = negatif ? -n : n;
  const euros = absolu / 100n;
  const centimes = absolu % 100n;
  const signe = negatif ? "−" : "";
  const centimesTexte = centimes.toString().padStart(2, "0");
  return `${signe}${FORMAT_ENTIER.format(euros)},${centimesTexte}${ESPACE_FINE}€`;
}

/** Somme de montants. Rend une chaîne décimale, jamais un nombre. */
export function addAmounts(...values: Int64[]): Int64 {
  return fromBigInt(values.reduce((total, v) => total + toBigInt(v), 0n));
}

/** Différence `a - b`, en chaîne décimale. Peut être négative. */
export function subAmounts(a: Int64, b: Int64): Int64 {
  return fromBigInt(toBigInt(a) - toBigInt(b));
}

/** Valeur absolue d'un montant. */
export function absAmount(value: Int64): Int64 {
  const n = toBigInt(value);
  return fromBigInt(n < 0n ? -n : n);
}

/** `-1`, `0` ou `1`, pour trier ou comparer deux montants. */
export function compareAmounts(a: Int64, b: Int64): -1 | 0 | 1 {
  const x = toBigInt(a);
  const y = toBigInt(b);
  return x < y ? -1 : x > y ? 1 : 0;
}

/** Vrai si le montant vaut zéro — le test à faire avant un `EmptyState`. */
export function isZeroAmount(value: Int64): boolean {
  return toBigInt(value) === 0n;
}

/** Vrai si le montant est strictement positif. */
export function isPositiveAmount(value: Int64): boolean {
  return toBigInt(value) > 0n;
}

/**
 * Part d'un montant dans un total, entre 0 et 1.
 *
 * C'est le seul endroit où un montant devient un `number`, et c'est légitime :
 * la valeur rendue pilote une largeur de jauge, pas une écriture comptable. La
 * division se fait quand même en `BigInt` (mise à l'échelle par 10^6 avant
 * conversion), pour qu'un total supérieur à 2^53 ne fausse pas le rapport.
 *
 * Un total nul rend `0` : une jauge vide, pas un `NaN` affiché en démonstration.
 */
export function ratio(part: Int64, whole: Int64): number {
  const total = toBigInt(whole);
  if (total === 0n) {
    return 0;
  }
  const ECHELLE = 1_000_000n;
  const brut = Number((toBigInt(part) * ECHELLE) / total) / Number(ECHELLE);
  return Math.min(1, Math.max(0, brut));
}

/**
 * Part d'un montant dans un total, en pourcentage entier de 0 à 100.
 *
 * À n'utiliser que pour un affichage calculé côté front. Quand l'API rend déjà
 * un `percent` (missions), c'est le sien qui fait foi : il est calculé sur les
 * montants exacts côté serveur.
 */
export function percentOf(part: Int64, whole: Int64): number {
  return Math.round(ratio(part, whole) * 100);
}

/**
 * Lit un montant de tokens saisi par un utilisateur — `3`, `3,5`, `3.05`,
 * `1 250,5` — et rend les centièmes en chaîne décimale attendue par l'API
 * (`"350"` pour `3,5`), ou `null` si la saisie n'est pas un montant positif à
 * deux décimales au plus.
 *
 * Tolère les espaces de groupement que l'utilisateur recopie depuis l'écran,
 * y compris l'espace fine insécable produite par `formatTokens`, et les deux
 * séparateurs décimaux : le clavier numérique d'un téléphone français donne la
 * virgule, celui d'un ordinateur portable donne souvent le point. Refuse une
 * troisième décimale plutôt que de l'arrondir : le centième est l'unité, et
 * un arrondi silencieux sur une saisie est exactement ce que le grand livre
 * s'interdit.
 */
export function parseTokenInput(saisie: string): Int64 | null {
  const separateurs = new RegExp(`[\\s${ESPACE_FINE}${ESPACE_INSECABLE}]`, "g");
  const nettoye = saisie.replace(separateurs, "");
  const lu = /^([0-9]+)(?:[.,]([0-9]{1,2}))?$/.exec(nettoye);
  if (lu === null) {
    return null;
  }
  const [, entiers, decimales = ""] = lu;
  // `padEnd` : « 3,5 » veut dire 3,50, pas 3,05.
  const centiemes = BigInt(entiers) * CENTIEMES_PAR_TOKEN + BigInt(decimales.padEnd(2, "0"));
  // Les zéros de tête disparaissent au passage par BigInt : "007" et "7"
  // désignent le même montant, et le contrat attend une forme canonique.
  return fromBigInt(centiemes);
}

/**
 * Convertit un nombre entier de tokens en centièmes, pour les constantes
 * écrites en tokens dans le code (`tokensToCents(5)` → `"500"`).
 */
export function tokensToCents(tokens: number | bigint): Int64 {
  return fromBigInt(BigInt(tokens) * CENTIEMES_PAR_TOKEN);
}
